import { useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    Plus, Trash2, ArrowUp, ArrowDown, GripVertical, AlertTriangle, Info, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Palette de couleurs par défaut proposée à l'ajout d'étape.
const STAGE_COLORS = ['#94A3B8', '#3B82F6', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#EC4899', '#06B6D4'];

// Slugification simple pour générer un stage_id lisible + unique.
const slugify = (s) => (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32) || `stage_${Math.random().toString(36).slice(2, 8)}`;

export function PipelineEditorDialog({ open, onOpenChange, theme, project, onSaved }) {
    const [stages, setStages] = useState([]);
    const [saving, setSaving] = useState(false);

    // Le titre et la source du pipeline dépendent du contexte (thème ou projet)
    const isProjectScope = !!project;
    const source = isProjectScope ? project : theme;
    const initialPipeline = isProjectScope
        ? (project?.pipeline || theme?.pipeline || [])
        : (theme?.pipeline || []);

    // Copie profonde à chaque ouverture
    useEffect(() => {
        if (open && source) {
            const copy = (initialPipeline || []).map((s) => ({
                id: s.id,
                name: s.name,
                color: s.color,
                order: s.order,
                max_days: s.max_days ?? null,
                _original: true,
            }));
            setStages(copy);
        }
    }, [open, source?.id]);

    const addStage = () => {
        const name = `Nouvelle étape ${stages.length + 1}`;
        let base = slugify(name);
        // Garantir unicité
        let id = base; let n = 1;
        while (stages.some((s) => s.id === id)) { id = `${base}_${n++}`; }
        setStages((prev) => [...prev, {
            id, name, color: STAGE_COLORS[prev.length % STAGE_COLORS.length],
            order: prev.length + 1, max_days: null, _original: false,
        }]);
    };

    const updateStage = (idx, patch) => {
        setStages((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    };

    const removeStage = (idx) => {
        if (stages.length <= 1) {
            toast.error('Le pipeline doit contenir au moins une étape');
            return;
        }
        const s = stages[idx];
        if (s._original) {
            // Prévenir de la migration potentielle
            if (!window.confirm(`Supprimer l'étape « ${s.name} » ?\n\nLes projets et tâches encore sur cette étape seront replacés sur la nouvelle 1ère étape du pipeline.`)) return;
        }
        setStages((prev) => prev.filter((_, i) => i !== idx));
    };

    const moveStage = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= stages.length) return;
        setStages((prev) => {
            const cp = [...prev];
            [cp[idx], cp[target]] = [cp[target], cp[idx]];
            return cp;
        });
    };

    const submit = async () => {
        if (stages.some((s) => !s.name?.trim())) {
            toast.error('Toutes les étapes doivent avoir un nom');
            return;
        }
        setSaving(true);
        try {
            const payload = stages.map((s, i) => ({
                id: s.id,
                name: s.name.trim(),
                color: s.color,
                order: i + 1,
                max_days: s.max_days != null && s.max_days !== '' ? Number(s.max_days) : null,
            }));
            const r = isProjectScope
                ? await hubAPI.updateProject(project.id, { pipeline: payload })
                : await hubAPI.updateTheme(theme.id, { pipeline: payload });
            toast.success('Pipeline mis à jour');
            onSaved?.(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    const originalIds = new Set((initialPipeline || []).map((s) => s.id));
    const removedCount = [...originalIds].filter((id) => !stages.some((s) => s.id === id)).length;

    // Cohérence deadline ↔ pipeline (uniquement en scope projet)
    const totalPipelineDays = stages.reduce((n, s) => n + (Number(s.max_days) || 0), 0);
    const projectStart = isProjectScope && project?.created_at ? new Date(project.created_at) : null;
    const suggestedDeadlineISO = (projectStart && totalPipelineDays > 0)
        ? new Date(projectStart.getTime() + totalPipelineDays * 86400000).toISOString()
        : null;
    const currentDeadlineISO = isProjectScope ? project?.deadline : null;
    // Considère un écart s'il est de plus de 24h
    const deadlineMismatch = !!(suggestedDeadlineISO && currentDeadlineISO
        && Math.abs(new Date(suggestedDeadlineISO).getTime() - new Date(currentDeadlineISO).getTime()) > 86400000);
    const noDeadlineYet = !!(isProjectScope && !currentDeadlineISO && suggestedDeadlineISO);
    const [alignDeadline, setAlignDeadline] = useState(false);
    // Auto-cocher si mismatch ou pas de deadline
    useEffect(() => {
        if (open && isProjectScope) setAlignDeadline(deadlineMismatch || noDeadlineYet);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, deadlineMismatch, noDeadlineYet]);

    // 💾 Enregistrer le pipeline courant comme modèle réutilisable dans l'espace de travail.
    // Ouvre un mini-dialogue interne pour saisir le titre + description optionnelle.
    const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
    const [tplTitle, setTplTitle] = useState('');
    const [tplDescription, setTplDescription] = useState('');
    const [savingTemplate, setSavingTemplate] = useState(false);

    const openSaveAsTemplate = () => {
        if (stages.some((s) => !s.name?.trim())) {
            toast.error("Complète le nom de chaque étape avant d'enregistrer le modèle");
            return;
        }
        setTplTitle('');
        setTplDescription('');
        setSaveAsTemplateOpen(true);
    };

    const submitSaveAsTemplate = async () => {
        const name = (tplTitle || '').trim();
        if (!name) {
            toast.error('Choisis un titre pour ton modèle');
            return;
        }
        setSavingTemplate(true);
        try {
            // On sauvegarde d'abord le pipeline courant sur le projet pour être sûr que
            // le modèle reflète EXACTEMENT ce que l'utilisateur voit à l'écran.
            const payload = stages.map((s, i) => ({
                id: s.id,
                name: s.name.trim(),
                color: s.color,
                order: i + 1,
                max_days: s.max_days != null && s.max_days !== '' ? Number(s.max_days) : null,
            }));
            await hubAPI.updateProject(project.id, { pipeline: payload });
            // Puis on crée le modèle partagé
            await hubAPI.savePipelineAsTemplate(project.id, { name, description: tplDescription || null });
            toast.success(`Modèle « ${name} » enregistré. Réutilisable à la création d'un projet.`);
            setSaveAsTemplateOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Enregistrement du modèle impossible");
        } finally {
            setSavingTemplate(false);
        }
    };

    // Enrichissement du submit pour aligner la deadline si demandé
    const originalSubmit = submit;
    const submitWithDeadline = async () => {
        if (!isProjectScope || !alignDeadline || !suggestedDeadlineISO) {
            return originalSubmit();
        }
        if (stages.some((s) => !s.name?.trim())) {
            toast.error('Toutes les étapes doivent avoir un nom');
            return;
        }
        setSaving(true);
        try {
            const payload = stages.map((s, i) => ({
                id: s.id,
                name: s.name.trim(),
                color: s.color,
                order: i + 1,
                max_days: s.max_days != null && s.max_days !== '' ? Number(s.max_days) : null,
            }));
            const r = await hubAPI.updateProject(project.id, { pipeline: payload, deadline: suggestedDeadlineISO });
            toast.success(`Pipeline mis à jour · Deadline alignée sur ${new Date(suggestedDeadlineISO).toLocaleDateString('fr-FR')}`);
            onSaved?.(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pipeline-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isProjectScope
                            ? `Pipeline du projet « ${project?.name} »`
                            : `Pipeline du thème « ${theme?.name} »`}
                    </DialogTitle>
                    <DialogDescription>
                        {isProjectScope
                            ? "Chaque projet a son propre pipeline. Vous pouvez adapter les étapes à ce projet précisément sans impacter les autres."
                            : "Modifiez les étapes du pipeline par défaut de ce thème. Les nouveaux projets créés hériteront de ce pipeline."}
                        {" Le délai max déclenche une alerte quand un projet dépasse cette durée sur une étape."}
                    </DialogDescription>
                </DialogHeader>

                {removedCount > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold">{removedCount} étape{removedCount > 1 ? 's' : ''} à supprimer</div>
                            Les projets ou tâches encore présents sur ces étapes seront replacés sur la <strong>nouvelle 1ère étape</strong> du pipeline.
                        </div>
                    </div>
                )}

                <div className="space-y-2" data-testid="pipeline-stages-list">
                    {stages.map((s, idx) => (
                        <div
                            key={s.id}
                            className="border border-slate-200 rounded-lg p-2 flex items-center gap-2 bg-white"
                            data-testid={`pipeline-stage-${s.id}`}
                        >
                            <div className="flex flex-col gap-0.5 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => moveStage(idx, -1)}
                                    disabled={idx === 0}
                                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    data-testid={`pipeline-move-up-${s.id}`}
                                    title="Monter"
                                >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveStage(idx, 1)}
                                    disabled={idx === stages.length - 1}
                                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    data-testid={`pipeline-move-down-${s.id}`}
                                    title="Descendre"
                                >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                            <div className="w-6 h-6 rounded-full shrink-0 border border-white ring-1 ring-slate-200" style={{ backgroundColor: s.color }}>
                                <input
                                    type="color"
                                    value={s.color}
                                    onChange={(e) => updateStage(idx, { color: e.target.value })}
                                    className="w-full h-full opacity-0 cursor-pointer"
                                    data-testid={`pipeline-color-${s.id}`}
                                    title="Couleur de l'étape"
                                />
                            </div>
                            <Input
                                value={s.name}
                                onChange={(e) => updateStage(idx, { name: e.target.value })}
                                placeholder="Nom de l'étape"
                                className="flex-1 min-w-0"
                                data-testid={`pipeline-name-${s.id}`}
                            />
                            <div className="flex items-center gap-1 shrink-0">
                                <Input
                                    type="number"
                                    min="1"
                                    value={s.max_days ?? ''}
                                    onChange={(e) => updateStage(idx, { max_days: e.target.value === '' ? null : Number(e.target.value) })}
                                    placeholder="—"
                                    className="w-16 text-center"
                                    data-testid={`pipeline-maxdays-${s.id}`}
                                    title="Délai max en jours avant alerte (laisser vide pour aucune limite)"
                                />
                                <span className="text-[10px] text-slate-400">j max</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeStage(idx)}
                                className="text-slate-300 hover:text-red-500 p-1"
                                data-testid={`pipeline-remove-${s.id}`}
                                title="Supprimer cette étape"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>

                <Button variant="outline" onClick={addStage} className="w-full gap-1" data-testid="pipeline-add-stage">
                    <Plus className="w-4 h-4" /> Ajouter une étape
                </Button>

                <div className="text-[11px] text-slate-500 flex items-start gap-1.5 pt-2 border-t">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-indigo-500" />
                    <div>
                        <strong>Astuce :</strong> gardez 4 à 6 étapes maximum pour rester lisible. Le délai max déclenche une alerte automatique dès qu&apos;un projet dépasse cette durée sur cette étape.
                    </div>
                </div>

                {/* Cohérence deadline ↔ pipeline (projet uniquement) */}
                {isProjectScope && totalPipelineDays > 0 && (
                    <div className={cn(
                        'rounded-md border p-3 text-xs space-y-2',
                        (deadlineMismatch || noDeadlineYet) ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
                    )} data-testid="pipeline-coherence-panel">
                        <div className="flex items-start gap-2">
                            <Info className={cn('w-4 h-4 mt-0.5 shrink-0', (deadlineMismatch || noDeadlineYet) ? 'text-amber-600' : 'text-emerald-600')} />
                            <div className="flex-1">
                                <div className="font-semibold text-slate-800">
                                    Durée totale du pipeline : <span className="text-indigo-700">{totalPipelineDays} jour{totalPipelineDays > 1 ? 's' : ''}</span>
                                </div>
                                {currentDeadlineISO && (
                                    <div className="text-slate-600 mt-0.5">
                                        Date butoir actuelle : {new Date(currentDeadlineISO).toLocaleDateString('fr-FR')}
                                    </div>
                                )}
                                {suggestedDeadlineISO && (
                                    <div className={cn('mt-0.5', (deadlineMismatch || noDeadlineYet) ? 'text-amber-800' : 'text-emerald-800')}>
                                        {noDeadlineYet
                                            ? `Suggestion : fixer la date butoir au ${new Date(suggestedDeadlineISO).toLocaleDateString('fr-FR')}`
                                            : deadlineMismatch
                                                ? `Recommandation : ${new Date(suggestedDeadlineISO).toLocaleDateString('fr-FR')} (cohérent avec le pipeline)`
                                                : `Cohérent avec le pipeline`}
                                    </div>
                                )}
                            </div>
                        </div>
                        {suggestedDeadlineISO && (deadlineMismatch || noDeadlineYet) && (
                            <label className="flex items-center gap-2 cursor-pointer text-slate-700 select-none" data-testid="pipeline-align-deadline">
                                <input
                                    type="checkbox"
                                    checked={alignDeadline}
                                    onChange={(e) => setAlignDeadline(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                                />
                                <span>
                                    Aligner la date butoir sur ce pipeline
                                    <span className="text-slate-500 ml-1">→ {new Date(suggestedDeadlineISO).toLocaleDateString('fr-FR')}</span>
                                </span>
                            </label>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                    {isProjectScope && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={openSaveAsTemplate}
                            disabled={saving || stages.length === 0}
                            className="gap-1 order-2 sm:order-1"
                            data-testid="pipeline-save-as-template"
                            title="Enregistrer ce pipeline comme modèle réutilisable dans l'espace de travail"
                        >
                            <Save className="w-4 h-4" /> Enregistrer comme modèle
                        </Button>
                    )}
                    <div className="flex items-center gap-2 order-1 sm:order-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
                        <Button onClick={submitWithDeadline} disabled={saving || stages.length === 0} data-testid="pipeline-submit" className={cn("gap-1")}>
                            {saving ? 'Enregistrement…' : (isProjectScope && alignDeadline && suggestedDeadlineISO ? 'Enregistrer pipeline + deadline' : 'Enregistrer le pipeline')}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>

            {/* Mini-dialogue interne : saisie du titre du modèle */}
            <Dialog open={saveAsTemplateOpen} onOpenChange={setSaveAsTemplateOpen}>
                <DialogContent className="max-w-md" data-testid="save-template-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Save className="w-5 h-5 text-indigo-600" />
                            Enregistrer comme modèle
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Ce pipeline sera disponible pour tout le monde dans cet espace de travail
                            lors de la création d&apos;un nouveau projet.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label htmlFor="tpl-title" className="text-sm">Titre du modèle *</Label>
                            <Input
                                id="tpl-title"
                                autoFocus
                                value={tplTitle}
                                onChange={(e) => setTplTitle(e.target.value)}
                                placeholder="Ex : Pipeline CEE Industriel personnalisé"
                                maxLength={80}
                                disabled={savingTemplate}
                                data-testid="save-template-title-input"
                            />
                            <p className="text-[11px] text-slate-400 mt-1">{tplTitle.length}/80 caractères</p>
                        </div>
                        <div>
                            <Label htmlFor="tpl-desc" className="text-sm">Description (facultatif)</Label>
                            <Textarea
                                id="tpl-desc"
                                value={tplDescription}
                                onChange={(e) => setTplDescription(e.target.value)}
                                placeholder="À quoi sert ce modèle ? Quand l'utiliser ?"
                                rows={3}
                                disabled={savingTemplate}
                                data-testid="save-template-desc-input"
                            />
                        </div>
                        <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2 text-[11px] text-indigo-800 flex items-start gap-2">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <div>
                                {stages.length} étape{stages.length > 1 ? 's' : ''} seront enregistrées avec
                                leur couleur et leur délai max. Partagé avec tous les membres de l&apos;espace.
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setSaveAsTemplateOpen(false)}
                            disabled={savingTemplate}
                            data-testid="save-template-cancel-btn"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={submitSaveAsTemplate}
                            disabled={savingTemplate || !tplTitle.trim()}
                            className="gap-1"
                            data-testid="save-template-confirm-btn"
                        >
                            <Save className="w-4 h-4" />
                            {savingTemplate ? 'Enregistrement…' : 'Enregistrer le modèle'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

export default PipelineEditorDialog;
